import React, { useState, useEffect, useRef } from 'react';
import { getCities } from '../../services/apiService';

// ===== Hangul 초성 추출 및 매칭 유틸 =====
const CHO = [
  'ㄱ','ㄲ','ㄴ','ㄷ','ㄸ','ㄹ','ㅁ','ㅂ','ㅃ','ㅅ','ㅆ','ㅇ','ㅈ','ㅉ','ㅊ','ㅋ','ㅌ','ㅍ','ㅎ'
];
const HANGUL_BASE = 0xac00;
const HANGUL_LAST = 0xd7a3;
const CHOSUNG_CYCLE = 588; // 21 * 28

const toChosung = (str = '') => {
  let res = '';
  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i);
    if (ch >= HANGUL_BASE && ch <= HANGUL_LAST) {
      const idx = Math.floor((ch - HANGUL_BASE) / CHOSUNG_CYCLE);
      res += CHO[idx] || str[i];
    } else {
      res += str[i];
    }
  }
  return res;
};

const matchKorean = (query, text) => {
  if (!query) return true;
  const q = query.trim();
  const t = text || '';
  // 일반 포함 검색
  if (t.includes(q)) return true;
  // 초성 검색
  const tCho = toChosung(t);
  return tCho.includes(q);
};

// ===== 도시/카테고리 선택 컴포넌트 (초성 검색 + SP 호출) =====
const CitySelector = ({ value, isOpen, onToggle, onChange, placeholder = "카테고리명 또는 초성(ㄷㅅ 등)으로 검색" }) => {
  const [cities, setCities] = useState([]); // [{코드, 코드명}]
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const wrapRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    const fetchCities = async () => {
      try {
        setLoading(true);
        setError('');
        const data = await getCities();
        if (!mounted) return;
        // API returns data.table with keys: 코드, 코드명
        setCities(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!mounted) return;
        setError('목록을 불러오는 중 오류가 발생했습니다.');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    // 최초 1회 로드 (열릴 때 로드하려면 isOpen 의존성 사용 가능)
    if (cities.length === 0) fetchCities();
    return () => { mounted = false; };
  }, []);

  // Outside click & ESC close
  useEffect(() => {
    const onDocMouseDown = (e) => {
      if (!isOpen) return;
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        onToggle();
      }
    };
    const onDocKeyDown = (e) => {
      if (!isOpen) return;
      if (e.key === 'Escape') {
        onToggle();
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onDocKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onDocKeyDown);
    };
  }, [isOpen, onToggle]);

  const filtered = cities.filter((c) => matchKorean(query, c.코드명));

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={onToggle}
        className="flex justify-between items-center w-full px-3 py-2 bg-white text-left focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
      >
        <span className={value ? 'text-gray-900' : 'text-gray-400'}>{value || '선택하세요'}</span>
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </button>
      {isOpen && (
        <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-80 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
          <div className="px-2 py-2 sticky top-0 bg-white border-b z-20">
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={placeholder}
              className="w-full px-2 py-1.5 border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          {loading && <div className="px-3 py-2 text-sm text-gray-500">불러오는 중...</div>}
          {error && <div className="px-3 py-2 text-sm text-red-500">{error}</div>}
          {!loading && filtered.length === 0 && (
            <div className="px-3 py-2 text-sm text-gray-500">검색 결과가 없습니다.</div>
          )}
          {!loading && filtered.map((c) => (
            <div
              key={c.코드}
              onClick={() => {
                onChange(c.코드명); // UI에는 코드명만 노출 및 저장
                onToggle(); // 선택 후 닫기
              }}
              className={`cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-blue-100 ${value === c.코드명 ? 'bg-blue-50 text-blue-700' : 'text-gray-900'}`}
            >
              {c.코드명}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CitySelector;
